'use client';

import { Box, Divider, Stack } from '@unity/cloud-ui';
import React from 'react';
import { z } from 'zod';
import { useTranslations } from 'next-intl';
import { Header } from '../header';
import { Code } from '../code';

const FieldSchema = z
  .object({
    name: z.string().optional(),
    title: z.string().optional(),
    type: z.string().optional(),
    default: z.string().optional(),
    required: z.boolean().optional(),
    children: z.any(),
  })
  .refine(
    (data) => {
      return (
        (data.title !== undefined && data.name === undefined) || (data.title === undefined && data.name !== undefined)
      );
    },
    {
      message: `Field must contain either 'title' or 'name', but not both.`,
    },
  )
  .transform((data) => {
    return {
      name: data.name || data.title,
      type: data.type,
      default: data.default,
      required: data.required,
      children: data.children,
    };
  });

type FieldProps = z.input<typeof FieldSchema>;

export function FieldGroup(props: { children: React.ReactNode }) {
  return (
    <Box>
      <Stack direction="column" spacing={2} divider={<Divider />}>
        {props.children}
      </Stack>
    </Box>
  );
}

export function Field(p: FieldProps) {
  const props = FieldSchema.parse(p);
  const t = useTranslations();

  return (
    <Box>
      <Stack direction="column" spacing={2}>
        <Stack direction="row" spacing={1}>
          <Box>
            <Header
              variant="h4"
              sx={{
                fontFamily: 'monospace',
              }}
            >
              <Box
                component="span"
                sx={{
                  color: ({ vars }) => vars.palette.primary.main,
                  fontWeight: ({ typography }) => typography.fontWeightBold,
                }}
              >
                {props.name}
              </Box>
            </Header>
          </Box>
          {props.type && (
            <Box>
              <Code>{props.type}</Code>
            </Box>
          )}
          {props.default && (
            <Box>
              <Code>{`${t('mdx:field:default')}: ${props.default}`}</Code>
            </Box>
          )}
          {props.required && (
            <Box>
              <Code
                sx={{
                  color: ({ vars }) => vars.palette.error.dark,
                  backgroundColor: ({ vars }) => vars.palette.error.lighter,
                }}
              >
                {t('mdx:field:required')}
              </Code>
            </Box>
          )}
        </Stack>
        {props.children}
      </Stack>
    </Box>
  );
}
